import React, { useMemo, useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

/**
 * Drive-style upload queue: first 3 active/failed rows + "+N more".
 */
const UploadQueue = ({
  items,
  onRetry,
  onCancelRemaining,
  onClose,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const { doneCount, total, inFlight, visible, moreCount } = useMemo(() => {
    const totalCount = items.length;
    const done = items.filter((i) => i.status === "done").length;
    const flying = items.some((i) => i.status === "uploading" || i.status === "queued");
    const activeOrFailed = items.filter(
      (i) => ["uploading", "queued", "failed", "rejected"].includes(i.status),
    );
    const show = activeOrFailed.slice(0, 3);
    return {
      doneCount: done,
      total: totalCount,
      inFlight: flying,
      visible: show,
      moreCount: Math.max(0, activeOrFailed.length - show.length),
    };
  }, [items]);

  if (!items.length) return null;

  const header = inFlight
    ? `Uploading ${doneCount} of ${total}`
    : `${total} upload${total === 1 ? "" : "s"} complete`;

  return (
    <div className="fixed bottom-4 right-4 z-40 w-80 overflow-hidden rounded-xl border border-theme-secondary bg-theme-main shadow-xl">
      <div className="flex items-center gap-2 border-b border-theme-secondary px-3 py-2">
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-theme-text">
          {header}
        </p>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="rounded p-1 text-theme-text opacity-60 hover:opacity-100"
          aria-label={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>
        <button
          type="button"
          disabled={inFlight}
          onClick={onClose}
          className="rounded p-1 text-theme-text opacity-60 hover:opacity-100 disabled:opacity-30"
          aria-label="Close upload queue"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      {!collapsed && (
        <div className="max-h-64 overflow-y-auto">
          {visible.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 border-b border-theme-secondary px-3 py-2 last:border-b-0"
            >
              <div className="h-8 w-8 shrink-0 overflow-hidden rounded bg-theme-secondary">
                {item.previewUrl ? (
                  <img src={item.previewUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] text-theme-text opacity-50">
                    file
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-theme-text">{item.name}</p>
                {item.status === "uploading" && (
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-theme-secondary">
                    <div
                      className="h-full bg-theme-primary transition-all"
                      style={{ width: `${Math.max(4, item.progress || 0)}%` }}
                    />
                  </div>
                )}
                {item.status === "queued" && (
                  <p className="text-[10px] text-theme-text opacity-50">Queued</p>
                )}
                {(item.status === "failed" || item.status === "rejected") && (
                  <p className="truncate text-[10px] text-red-500">{item.error || "Failed"}</p>
                )}
                {item.status === "done" && (
                  <p className="text-[10px] text-theme-text opacity-50">Done</p>
                )}
              </div>
              {item.status === "failed" && (
                <button
                  type="button"
                  onClick={() => onRetry(item.id)}
                  className="shrink-0 text-[10px] font-semibold text-theme-primary"
                >
                  Retry
                </button>
              )}
            </div>
          ))}
          {moreCount > 0 && (
            <p className="px-3 py-2 text-xs text-theme-text opacity-60">
              + {moreCount} more
            </p>
          )}
          {inFlight && (
            <button
              type="button"
              onClick={onCancelRemaining}
              className="w-full border-t border-theme-secondary px-3 py-2 text-left text-xs font-semibold text-theme-text opacity-70 hover:opacity-100"
            >
              Cancel remaining
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadQueue;
